/**
 * Which sidebar section (and so which sidebar mode: Operations, CRM, Finance, Intelligence, Admin) a route belongs to.
 *
 * 9 Oct review: the sidebar switched to Operations after clicking CRM, Finance or Intelligence items whose URL did
 * not start with a known word (/suppliers/rates, /expenses, /compliance, /knowledge-base ...), and the current page's
 * section was not kept open. The answer now comes from the menu itself: the item whose path matches the route (exact
 * first, then the longest parent path). When the route is in the mode the person is already using, the mode stays.
 */

export interface RouteSectionItem {
  path: string;
}

export interface RouteSection<TTab extends string = string> {
  id: string;
  tab: TTab;
  items: readonly RouteSectionItem[];
}

const EXACT = 10_000;
const QUERY_MATCH = 5_000;

function splitPath(path: string): { pathname: string; query: URLSearchParams | null } {
  const [withoutHash] = path.split('#');
  const queryIndex = withoutHash.indexOf('?');
  if (queryIndex < 0) return { pathname: withoutHash, query: null };
  return {
    pathname: withoutHash.slice(0, queryIndex),
    query: new URLSearchParams(withoutHash.slice(queryIndex + 1)),
  };
}

function queryMatches(itemQuery: URLSearchParams | null, search: string): boolean {
  if (!itemQuery) return false;
  const current = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  let any = false;
  for (const [key, value] of itemQuery.entries()) {
    any = true;
    if (current.get(key) !== value) return false;
  }
  return any;
}

/** 0 when the menu item does not cover the route; higher is a closer match. */
export function scoreNavigationItemForRoute(itemPath: string, pathname: string, search = ''): number {
  const { pathname: base, query } = splitPath(itemPath);
  if (!base) return 0;
  // An item whose query matches the page's query wins; one whose query differs ranks just below the plain item.
  const bonus = queryMatches(query, search) ? QUERY_MATCH : query ? -1 : 0;
  if (base.includes(':')) {
    const prefix = base.split(':')[0];
    return prefix.length > 1 && pathname.startsWith(prefix) ? Math.max(1, prefix.length + bonus) : 0;
  }
  if (pathname === base) return EXACT + base.length + bonus;
  const parent = base.endsWith('/') ? base : `${base}/`;
  if (base !== '/' && pathname.startsWith(parent)) return Math.max(1, base.length + bonus);
  return 0;
}

/** The sections holding the best-matching menu item for the route (more than one when an item is listed twice). */
export function findRouteSections<TSection extends RouteSection>(
  sections: readonly TSection[],
  pathname: string,
  search = '',
): TSection[] {
  let best = 0;
  let matches: TSection[] = [];
  for (const section of sections) {
    const score = section.items.reduce(
      (max, item) => Math.max(max, scoreNavigationItemForRoute(item.path, pathname, search)),
      0,
    );
    if (score === 0) continue;
    if (score > best) {
      best = score;
      matches = [section];
    } else if (score === best) {
      matches.push(section);
    }
  }
  return matches;
}

/**
 * The sidebar mode for a route. Keeps the current mode when the route is in it; otherwise the mode of the menu item
 * that matches; otherwise the fallback (a URL-word guess) or, failing that, the current mode. Never jumps to a
 * default mode for a route the menu does not know.
 */
export function resolveRouteTab<TTab extends string>(
  sections: readonly RouteSection<TTab>[],
  pathname: string,
  search: string,
  currentTab: TTab,
  fallbackTab?: TTab | null,
): TTab {
  const matches = findRouteSections(sections, pathname, search);
  if (matches.length > 0) {
    return matches.some((section) => section.tab === currentTab) ? currentTab : matches[0].tab;
  }
  return fallbackTab ?? currentTab;
}

/** The section to keep open for the route within the sections shown (the current mode). */
export function findRouteSectionId(
  sections: readonly RouteSection[],
  pathname: string,
  search = '',
): string | null {
  return findRouteSections(sections, pathname, search)[0]?.id ?? null;
}
